import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebaseConfig/firebase";
import axios from "axios";

// Material UI Components
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  AlertTitle,
  Container,
  Card,
  CardContent,
  Snackbar,
  Fab,
  ToggleButtonGroup,
  ToggleButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Popover
} from "@mui/material";

// Icons
import {
  WhatsApp,
  Delete,
  Edit,
  Add,
  CalendarMonth,
  Phone,
  Notes,
  EventAvailable,
  ViewDay,
  ViewWeek,
  ViewModule,
  ArrowBackIos,
  ArrowForwardIos
} from "@mui/icons-material";

const token = process.env.REACT_APP_API_KEYTOKEN;
const mytoken = process.env.REACT_APP_API_KEYMYTOKEN;
const phidtoken = process.env.REACT_APP_API_KEYphidtoken;

function Calendar() {
  // State variables
  const [open, setOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allAppointments, setAllAppointments] = useState([]);
  const [displayAppointments, setDisplayAppointments] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [viewMode, setViewMode] = useState("day"); // Changed default to day view
  const [loading, setLoading] = useState(true);
  const [selectedDayInWeek, setSelectedDayInWeek] = useState(new Date());
  const [weekDays, setWeekDays] = useState([]);
  const [monthDays, setMonthDays] = useState([]);
  const [appointmentCounts, setAppointmentCounts] = useState({});
  const [dataLoadStats, setDataLoadStats] = useState({
    total: 0,
    filtered: 0
  });
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [selectedDateForPicker, setSelectedDateForPicker] = useState(new Date());
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  let navigate = useNavigate();
  const location = useLocation();

  // Format the current date for display and database queries
  // Use a timezone-safe approach to prevent date shifting
  const formatDateForDB = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display to user
  const formatDateForDisplay = (dateString) => {
    const dateParts = dateString.split("-");
    return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
  };

  // Get formatted date for database
  const currentDateForDB = formatDateForDB(currentDate);
  
  // Format date for display
  const formattedDisplayDate = formatDateForDisplay(currentDateForDB);

  // Get day name
  const getDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };
  
  const currentDayName = getDayName(currentDate);

  // Initialize week days when date or view mode changes
  useEffect(() => {
    if (viewMode === "week") {
      generateWeekDays(new Date(currentDate));
    } else if (viewMode === "month") {
      generateMonthDays(currentDate);
    }
  }, [currentDate, viewMode]);

  // Generate array of week days for weekly view
  const generateWeekDays = (date) => {
    // Create a copy of the date to avoid modifying the original
    const dateCopy = new Date(date);
    
    const dayOfWeek = dateCopy.getDay();
    const diff = dateCopy.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // adjust when Sunday
    const monday = new Date(dateCopy);
    monday.setDate(diff);
    
    let weekDaysArray = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      weekDaysArray.push(day);
    }
    
    setWeekDays(weekDaysArray);
    setSelectedDayInWeek(date);
  };

  // Generate array of days for monthly view
  const generateMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of month
    const firstDayOfMonth = new Date(year, month, 1);
    // Last day of month
    const lastDayOfMonth = new Date(year, month + 1, 0);
    // Day of week of first day (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfWeek = firstDayOfMonth.getDay();
    
    // Calculate required offset to start grid with Monday
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    // Create array to hold all visible days on calendar
    let daysArray = [];
    
    // Add days from previous month to fill in start of calendar
    const lastDayPrevMonth = new Date(year, month, 0).getDate();
    for (let i = 0; i < offset; i++) {
      const prevMonthDate = new Date(year, month - 1, lastDayPrevMonth - offset + i + 1);
      daysArray.push({
        date: prevMonthDate,
        isCurrentMonth: false
      });
    }
    
    // Add days from current month
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const currentMonthDate = new Date(year, month, day);
      daysArray.push({
        date: currentMonthDate,
        isCurrentMonth: true
      });
    }
    
    // Add days from next month to fill grid (we want a total of 42 cells for 6 weeks)
    const remainingCells = 42 - daysArray.length;
    for (let day = 1; day <= remainingCells; day++) {
      const nextMonthDate = new Date(year, month + 1, day);
      daysArray.push({
        date: nextMonthDate,
        isCurrentMonth: false
      });
    }
    
    setMonthDays(daysArray);
  };

  // Handle changing view mode
  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  // Navigate to previous date/week/month
  const goToPrevious = () => {
    let newDate = new Date(currentDate);
    
    if (viewMode === "day") {
      newDate.setDate(newDate.getDate() - 1);
    } else if (viewMode === "week") {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewMode === "month") {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    
    setCurrentDate(newDate);
  };

  // Navigate to next date/week/month
  const goToNext = () => {
    let newDate = new Date(currentDate);
    
    if (viewMode === "day") {
      newDate.setDate(newDate.getDate() + 1);
    } else if (viewMode === "week") {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewMode === "month") {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    
    setCurrentDate(newDate);
  };

  // Go to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle date picker
  const handleDatePickerOpen = (event) => {
    setDatePickerAnchor(event.currentTarget);
    setSelectedDateForPicker(currentDate);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (event) => {
    const newDate = new Date(event.target.value);
    setSelectedDateForPicker(newDate);
  };

  const handleDateConfirm = () => {
    setCurrentDate(selectedDateForPicker);
    setDatePickerAnchor(null);
  };

  // Select day from week view
  const selectDayInWeek = (day) => {
    setSelectedDayInWeek(day);
    
    // Filter appointments for the selected day
    const dayFormatted = formatDateForDB(day);
    const filteredAppointments = allAppointments.filter(
      appointment => appointment.appointmentDate === dayFormatted
    );
    
    setDisplayAppointments(filteredAppointments);
  };

  // Select day from month view - FIXED
  const selectDayInMonth = (day) => {
    // Create a new date object with the exact year, month, and day values
    // This ensures we don't have any timezone issues
    const exactDate = new Date();
    exactDate.setFullYear(day.date.getFullYear());
    exactDate.setMonth(day.date.getMonth());
    exactDate.setDate(day.date.getDate());
    // Set time to noon to avoid any timezone edge cases
    exactDate.setHours(12, 0, 0, 0);
    
    setCurrentDate(exactDate);
    setViewMode("day");
  };
  
  // Get appointments count for a specific day
  const getAppointmentsForDate = (date) => {
    const dateFormatted = formatDateForDB(date);
    return appointmentCounts[dateFormatted] || 0;
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (appointment) => {
    setAppointmentToDelete(appointment);
    setOpen(true);
  };

  // Handle dialog actions
  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseYES = async () => {
    try {
      if (appointmentToDelete) {
        await deleteDoc(doc(db, "AppointmentDetails", appointmentToDelete.docid));
        showAlertMessage("success", "Deleted", "Appointment deleted successfully");
      }
    } catch (error) {
      showAlertMessage("error", "Error", "Could not delete appointment");
      console.error("Error deleting document: ", error);
    }
    setOpen(false);
  };

  // Show alert message helper
  const showAlertMessage = (type, title, message) => {
    setAlertType(type);
    setAlertTitle(title);
    setAlertMessage(message);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  // Fetch appointments based on view mode
  useEffect(() => {
    const appointmentData = collection(db, "AppointmentDetails");
    let q;
    
    setLoading(true);
    
    // Create query based on the current view mode
    if (viewMode === "day") {
      // For day view, only fetch appointments for the specific day
      const formattedDate = formatDateForDB(currentDate);
      q = query(
        appointmentData,
        where("date", "==", formattedDate),
        orderBy("time", "asc")
      );
      
    } else if (viewMode === "week" && weekDays.length > 0) {
      // For week view, fetch appointments for the entire week
      const startDate = formatDateForDB(weekDays[0]);
      const endDate = formatDateForDB(weekDays[6]);
      
      q = query(
        appointmentData,
        where("date", ">=", startDate),
        where("date", "<=", endDate),
        orderBy("date", "asc"),
        orderBy("time", "asc")
      );
      
    } else if (viewMode === "month") {
      // For month view, fetch appointments for the entire month
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      // First day of month
      const firstDayOfMonth = new Date(year, month, 1);
      // Last day of month
      const lastDayOfMonth = new Date(year, month + 1, 0);
      
      const startDate = formatDateForDB(firstDayOfMonth);
      const endDate = formatDateForDB(lastDayOfMonth);
      
      q = query(
        appointmentData,
        where("date", ">=", startDate),
        where("date", "<=", endDate),
        orderBy("date", "asc"),
        orderBy("time", "asc")
      );
      
    } else {
      // Default query if no specific view is active yet
      q = query(
        appointmentData,
        orderBy("date", "asc"),
        orderBy("time", "asc")
      );
    }

    // Execute the query
    const unsub = onSnapshot(q, (snap) => {
      let appointmentsArray = [];
      let counts = {};

      snap.forEach((doc) => {
        const apptDate = doc.get("date");
        
        // Update counts for this date
        if (!counts[apptDate]) {
          counts[apptDate] = 0;
        }
        counts[apptDate]++;
        
        appointmentsArray.push({
          ...doc.data(),
          id: doc.get("patientId"),
          ptName: doc.get("name"),
          docid: doc.id,
          timeString: doc.get("timeString"),
          phoneNumber: doc.get("phNumber"),
          photoURL: doc.get("photoURL"),
          duration: doc.get("duration"),
          reason: doc.get("reason"),
          appointmentDate: apptDate,
          appointmentTime: doc.get("time"),
          created: doc.get("created"),
        });
      });

      // Update data load statistics
      setDataLoadStats({
        total: snap.size,
        filtered: appointmentsArray.length
      });
      
      setAllAppointments(appointmentsArray);
      setAppointmentCounts(counts);
      setLoading(false);
      
      // Update display appointments based on the view mode
      updateDisplayAppointments(appointmentsArray);
    });

    return () => {
      unsub();
    };
  }, [viewMode, currentDate, weekDays]);
  
  // Helper function to update displayed appointments
  const updateDisplayAppointments = (appointments) => {
    if (appointments.length === 0) {
      setDisplayAppointments([]);
      return;
    }
    
    let filteredAppointments = [];
    
    if (viewMode === "day") {
      // Filter appointments for the current day
      filteredAppointments = appointments.filter(
        appointment => appointment.appointmentDate === formatDateForDB(currentDate)
      );
    } else if (viewMode === "week" && weekDays.length > 0) {
      // Filter appointments for the selected day in the week
      const selectedDayFormatted = formatDateForDB(selectedDayInWeek);
      filteredAppointments = appointments.filter(
        appointment => appointment.appointmentDate === selectedDayFormatted
      );
    } else {
      // For month view, we'll just use the appointments for the selected day
      filteredAppointments = appointments.filter(
        appointment => appointment.appointmentDate === formatDateForDB(currentDate)
      );
    }
    
    setDisplayAppointments(filteredAppointments);
  };

  // Open reminder confirmation dialog
  const openReminderDialog = (patient) => {
    setSelectedPatient(patient);
    setReminderOpen(true);
  };

  // Close reminder dialog
  const closeReminderDialog = () => {
    setReminderOpen(false);
  };

  // Confirm and send WhatsApp reminder message
  const confirmAndSendMessage = () => {
    if (selectedPatient) {
      sendMessage(
        selectedPatient.ptName,
        selectedPatient.phoneNumber,
        selectedPatient.appointmentDate,
        selectedPatient.appointmentTime
      );
    }
    setReminderOpen(false);
  };

  // Send WhatsApp reminder message
  const sendMessage = (name, phNumber, date, time) => {
    const militaryTime = time;
    const hours = Math.floor(militaryTime / 100);
    const minutes = militaryTime % 100;
    const timeString = `${hours % 12 || 12}:${minutes
      .toString()
      .padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}`;

    let originalDate = date;
    let splitDate = originalDate.split("-");
    let convertedDate = splitDate[2] + "-" + splitDate[1] + "-" + splitDate[0];



      // WhatsApp notification
        try {
          axios
            .post(
              "https://us-central1-patradentalclinic.cloudfunctions.net/expressApi/appointmentReminder",
              {
                name: name,
                phno: phNumber,
                convertedDate: convertedDate,
                timeString: timeString,
              },
              {
                headers: {
                  "Content-type": "application/json; charset=UTF-8",
                },
              }
            )
            .then((res) => {
              showAlertMessage("success", "Message Sent", "Reminder sent successfully");
            });
        } catch (whatsappError) {
          console.error("WhatsApp notification failed:", whatsappError);
          // Continue even if WhatsApp fails
        }
    

  
  };

  // Navigate to patient profile
  const goToPatientProfile = async (item) => {
    try {
      // Fetch complete patient data from patientProfile collection
      const patientProfileRef = doc(db, "patientProfile", item.docid);
      const patientDoc = await getDoc(patientProfileRef);
      
      if (patientDoc.exists()) {
        const patientData = patientDoc.data();
        navigate("/profile", {
          state: {
            id: 1,
            ptid: patientData.ptId || item.id,
            opdId: patientData.opdId || item.id,
            originalptId: patientData.ptId,
            name: patientData.name || item.ptName,
            phoneNumber: patientData.phNumber || item.phoneNumber,
            age: patientData.age,
            gender: patientData.gender,
            photoURL: patientData.photoURL || item.photoURL,
            TotalAmount: patientData.TotalAmount,
            totalPaid: patientData.paid,
            xrayFolderNumber: patientData.xrayFolderNumber,
          },
        });
      } else {
        // Fallback to appointment data if patient profile not found
        navigate("/profile", {
          state: {
            id: 1,
            ptid: item.id,
            opdId: item.id,
            name: item.ptName,
            phoneNumber: item.phoneNumber,
            photoURL: item.photoURL,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching patient data:", error);
      // Fallback to appointment data
      navigate("/profile", {
        state: {
          id: 1,
          ptid: item.id,
          opdId: item.id,
          name: item.ptName,
          phoneNumber: item.phoneNumber,
          photoURL: item.photoURL,
        },
      });
    }
  };

  // Navigate to reschedule appointment
  const goToReschedule = (item) => {
    navigate("/updateAppointment", {
      state: {
        docid: item.docid,
        name: item.ptName,
        time: item.timeString,
        date: item.date,
        duration: item.duration,
        phoneNumber: item.phoneNumber,
        reason: item.reason,
      },
    });
  };

  // Get Header title based on view mode
  const getHeaderTitle = () => {
    if (viewMode === "day") {
      return (
        <>
          <Typography variant="h6" sx={{ fontWeight: "600", fontSize: "1.1rem" }}>
            {formattedDisplayDate}
          </Typography>
          <Chip 
            label={currentDayName} 
            size="small" 
            sx={{ 
              ml: 1, 
              bgcolor: "rgba(255,255,255,0.2)", 
              color: "white",
              fontSize: "0.7rem",
              height: "20px"
            }} 
          />
        </>
      );
    } else if (viewMode === "week") {
      // Check if weekDays array has been populated
      if (weekDays.length === 0) {
        return (
          <Typography variant="h6" sx={{ fontWeight: "600", fontSize: "1.1rem" }}>
            Weekly View
          </Typography>
        );
      }
      
      const firstDay = weekDays[0];
      const lastDay = weekDays[6];
      const formattedFirstDay = `${firstDay.getDate()}/${firstDay.getMonth() + 1}`;
      const formattedLastDay = `${lastDay.getDate()}/${lastDay.getMonth() + 1}/${lastDay.getFullYear()}`;
      
      return (
        <Typography variant="h6" sx={{ fontWeight: "600", fontSize: "1.1rem" }}>
          Week {formattedFirstDay} - {formattedLastDay}
        </Typography>
      );
    } else if (viewMode === "month") {
      return (
        <Typography variant="h6" sx={{ fontWeight: "600", fontSize: "1.1rem" }}>
          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Typography>
      );
    }
  };

  // Render appointment card
  const renderAppointmentCard = (item) => (
    <Card 
      key={item.docid} 
      sx={{ 
        mb:1,
        borderRadius: "16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
        border: item.isRescheduled ? "2px solid #f57c00" : item.type === "pending" ? "2px solid #ff9800" : "1px solid rgba(0,0,0,0.06)",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          transform: "translateY(-4px)",
          borderColor: item.isRescheduled ? "rgba(245, 124, 0, 0.3)" : item.type === "pending" ? "rgba(255, 152, 0, 0.3)" : "rgba(25, 118, 210, 0.2)"
        }
      }}
    >
      <CardContent sx={{ p: 0 ,'&:last-child': { pb: 0 } }}>
        <Box sx={{ 
          p: { xs: 1.5, sm: 1.75 }, 
          // height:"100%",
          display: "flex", 
          alignItems: "center",
          // justifyContent: "space-between",
          gap: { xs: 1.5, sm: 2 }
        }}>
          {/* Time Section - Left Side */}
          <Box sx={{ 
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: { xs: "60px", sm: "70px" },
            flexShrink: 0,
            gap: 0.3
          }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                fontWeight: 700,
                color: item.isRescheduled ? "#f57c00" : item.type === "pending" ? "#ff9800" : "#1976d2",
                lineHeight: 1,
                letterSpacing: "-0.02em"
              }}
            >
              {item.timeString}
            </Typography>
            
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                color: "#666",
                fontWeight: 500,
                textAlign: "center"
              }}
            >
              {item.duration}
            </Typography>
            
            {item.isRescheduled && (
              <Chip
                label="Rescheduled"
                size="small"
                sx={{
                  fontSize: "0.6rem",
                  height: "18px",
                  bgcolor: "#fff3e0",
                  color: "#f57c00",
                  fontWeight: 600,
                  mt: 0.5
                }}
              />
            )}

            {!item.isRescheduled && item.type === "pending" && (
              <Chip
                label="Pending"
                size="small"
                sx={{
                  fontSize: "0.6rem",
                  height: "18px",
                  bgcolor: "#fff8e1",
                  color: "#ff9800",
                  fontWeight: 600,
                  mt: 0.5
                }}
              />
            )}
          </Box>
          
          {/* Vertical Divider */}
          <Box sx={{
       width: "0px",
          height: { xs: "60px", sm: "65px" },
          borderLeft: "2px dotted rgba(0,0,0,0.15)",
          borderRadius:'50px',
          flexShrink: 0
          }} />
          
          {/* Patient Info Section - Center/Right */}
          <Box sx={{ 
            flex: 1, 
            display: "flex",
            flexDirection: "column",
            gap: { xs: 0.75, sm: 1 },
            minWidth: 0
          }}>
            {/* Name and Avatar Row */}
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 1.2
            }}>
              <Avatar 
                src={item.photoURL}
                onClick={() => goToPatientProfile(item)}
                sx={{ 
                  width: { xs: 36, sm: 42 }, 
                  height: { xs: 36, sm: 42 },
                  bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  fontSize: { xs: "0.95rem", sm: "1.1rem" },
                  fontWeight: "700",
                  cursor: "pointer",
                  border: "2px solid #fff",
                  boxShadow: "0 3px 10px rgba(0,0,0,0.12)",
                  transition: "all 0.3s ease",
                  flexShrink: 0,
                  "&:hover": {
                    transform: "scale(1.08)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.18)"
                  }
                }}
              >
                {item.ptName?.charAt(0).toUpperCase()}
              </Avatar>
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="h6" 
                  onClick={() => goToPatientProfile(item)}
                  sx={{ 
                    fontWeight: 700, 
                    cursor: "pointer",
                    fontSize: { xs: "0.95rem", sm: "1.05rem" },
                    color: "#1a1a1a",
                    lineHeight: 1.3,
                    mb: 0.2,
                    transition: "color 0.2s",
                    "&:hover": {
                      color: "#1976d2"
                    }
                  }}
                >
                  {item.ptName}
                </Typography>
                
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center",
                  gap: 0.5
                }}>
                  <Phone sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.9rem" }, 
                    color: "#1976d2" 
                  }} />
                   <Typography 
                  component="a"
                  href={`tel:${item.phoneNumber}`}
                  variant="body2" 
                  sx={{ 
                    color: "#1976d2",
                    fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "#1565c0"
                    }
                  }}
                >
                  {item.phoneNumber}
                </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Reason Section */}
            <Box sx={{ 
              display: "flex", 
              alignItems: "flex-start",
              gap: 0.6,
              bgcolor: "#f8f9fa",
              p: { xs: 0.75, sm: 1 },
              borderRadius: "8px",
              border: "1px solid rgba(0,0,0,0.05)"
            }}>
              <Notes sx={{ 
                fontSize: { xs: "0.8rem", sm: "0.9rem" }, 
                color: "#1976d2",
                mt: 0.1,
                flexShrink: 0
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: { xs: "0.75rem", sm: "0.8rem" },
                  lineHeight: 1.5,
                  color: "#4a4a4a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical"
                }}
              >
                {item.reason}
              </Typography>
            </Box>
          </Box>
          
          {/* Action Buttons - Right Side */}
          <Box sx={{ 
            display: "flex", 
            flexDirection: "column", 
            gap: 0.6,
            alignItems: "center",
            flexShrink: 0
          }}>
            <IconButton 
              size="small"
              onClick={() => goToReschedule(item)}
              sx={{ 
                color: "#1976d2",
                // bgcolor: "#1976d2",
                width: { xs: "32px", sm: "36px" },
                height: { xs: "32px", sm: "36px" },
                boxShadow: "0 2px 6px rgba(25, 118, 210, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "#1565c0",
                  boxShadow: "0 3px 10px rgba(25, 118, 210, 0.4)",
                  transform: "scale(1.08)"
                }
              }}
            >
              <Edit sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }} />
            </IconButton>
            
         
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
  // Render weekly view
  const renderWeeklyView = () => (
    <>
      {weekDays.length > 0 ? (
        <>
          {/* Header with arrows and date range */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <IconButton
              onClick={() => {
                const newDate = new Date(weekDays[0]);
                newDate.setDate(newDate.getDate() - 7);
                setCurrentDate(newDate);
              }}
              sx={{
                bgcolor: "rgba(0,0,0,0.04)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.08)" }
              }}
            >
              <ArrowBackIos fontSize="small" />
            </IconButton>
  
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </Typography>
  
            <IconButton
              onClick={() => {
                const newDate = new Date(weekDays[6]);
                newDate.setDate(newDate.getDate() + 1);
                setCurrentDate(newDate);
              }}
              sx={{
                bgcolor: "rgba(0,0,0,0.04)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.08)" }
              }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </Box>
  
          {/* Responsive Week View: scroll on mobile, center on desktop */}
          <Box
            sx={{
              display: { xs: "flex", md: "flex" },
              justifyContent: { xs: "flex-start", md: "center" },
              overflowX: { xs: "auto", md: "unset" },
              gap: 1,
              pb: 1,
              mb: 2,
              scrollbarWidth: "thin"
            }}
          >
            {weekDays.map((day, index) => {
              const isSelected = day.toDateString() === selectedDayInWeek.toDateString();
              const isToday = day.toDateString() === new Date().toDateString();
              const appointmentCount = getAppointmentsForDate(day);
  
              return (
                <Box
                  key={index}
                  onClick={() => selectDayInWeek(day)}
                  sx={{
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: "60px",
                    px: 0.5,
                    py: 1,
                    cursor: "pointer",
                    backgroundColor: isSelected ? "rgba(25, 118, 210, 0.08)" : "transparent",
                    borderRadius: "6px",
                    border: isSelected ? "1px solid rgba(25, 118, 210, 0.3)" : "1px solid transparent",
                    transition: "all 0.2s"
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isToday ? "#1976d2" : "text.secondary",
                      fontWeight: isToday ? 600 : 400
                    }}
                  >
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                  </Typography>
  
                  <Avatar
                    sx={{
                      my: 0.5,
                      width: 30,
                      height: 30,
                      fontSize: "0.75rem",
                      bgcolor: isToday ? "#1976d2" :
                        isSelected ? "rgba(25, 118, 210, 0.1)" :
                          "rgba(0, 0, 0, 0.04)",
                      color: isToday ? "white" :
                        isSelected ? "#1976d2" :
                          "text.primary"
                    }}
                  >
                    {day.getDate()}
                  </Avatar>
  
                  {appointmentCount > 0 && (
                    <Chip
                      label={`${appointmentCount} appt${appointmentCount > 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        height: "16px",
                        fontSize: "0.6rem",
                        mt: 0.3,
                        bgcolor: "#f0f7ff",
                        color: "#1976d2",
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
  
      {/* Appointments list for selected day */}
      <Box sx={{ mb: 8 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333" }}>
            {selectedDayInWeek.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {displayAppointments.length > 0
              ? `${displayAppointments.length} appointment${displayAppointments.length > 1 ? 's' : ''} scheduled`
              : "No appointments scheduled"
            }
          </Typography>
        </Box>
  
        {displayAppointments.length > 0 ? (
          displayAppointments.map(item => renderAppointmentCard(item))
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4
            }}
          >
            <EventAvailable sx={{ fontSize: "3rem", color: "#e0e0e0", mb: 1 }} />
            <Typography variant="body1" sx={{ color: "#999" }}>
              No appointments for this day
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
  
  
  // Render monthly view
  const renderMonthlyView = () => (
    <Box sx={{ mb: 8 }}>
      {/* Data statistics box */}
      <Box sx={{ 
        mb: 2, 
        p: 2, 
        bgcolor: "#f5f8fa", 
        borderRadius: "8px", 
        border: "1px solid rgba(0,0,0,0.08)"
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
          <strong>Data Load Statistics</strong>
        </Typography>
        <Typography variant="body2" sx={{ color: "#666" }}>
          Total appointments loaded: <strong>{dataLoadStats.total}</strong><br />
          Filtered for current view: <strong>{dataLoadStats.filtered}</strong><br />
          Current month: <strong>{currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong>
        </Typography>
      </Box>
      
      <TableContainer component={Paper} elevation={0} sx={{ 
        borderRadius: "12px", 
        border: "1px solid rgba(0,0,0,0.08)",
        overflow: "hidden"
      }}>
        <Table>
          <TableHead>
            <TableRow>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <TableCell 
                  key={day} 
                  align="center"
                  sx={{ 
                    bgcolor: "#f5f5f5", 
                    fontWeight: 600, 
                    py: 1.5,
                    borderBottom: "1px solid rgba(0,0,0,0.08)"
                  }}
                >
                  {day}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 6 }, (_, weekIndex) => (
              <TableRow key={weekIndex}>
                {Array.from({ length: 7 }, (_, dayIndex) => {
                  const dayObj = monthDays[weekIndex * 7 + dayIndex];
                  if (!dayObj) return <TableCell key={dayIndex} />;
                  
                  const { date, isCurrentMonth } = dayObj;
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isSelected = date.toDateString() === currentDate.toDateString();
                  const appointmentCount = getAppointmentsForDate(date);
                  
                  return (
                    <TableCell 
                      key={dayIndex} 
                      onClick={() => selectDayInMonth(dayObj)}
                      align="center"
                      sx={{
                        bgcolor: isSelected ? "rgba(25, 118, 210, 0.08)" : 
                                isToday ? "rgba(25, 118, 210, 0.04)" : 
                                "transparent",
                        color: !isCurrentMonth ? "text.disabled" : 
                                isToday ? "#1976d2" : 
                                "text.primary",
                        fontWeight: isToday ? 600 : 400,
                        cursor: "pointer",
                        p: 1,
                        height: "70px",
                        transition: "all 0.2s",
                        borderLeft: dayIndex === 0 ? "none" : "1px solid rgba(0,0,0,0.04)",
                        borderBottom: "1px solid rgba(0,0,0,0.04)",
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.02)"
                        }
                      }}
                    >
                      <Box sx={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        alignItems: "center",
                        height: "100%"
                      }}>
                        {isToday ? (
                          <Avatar 
                            sx={{ 
                              width: 28, 
                              height: 28, 
                              fontSize: "0.75rem",
                              mb: 0.5,
                              bgcolor: "#1976d2",
                              color: "white"
                            }}
                          >
                            {date.getDate()}
                          </Avatar>
                        ) : (
                          <Typography variant="body2">
                            {date.getDate()}
                          </Typography>
                        )}
                        
                        {appointmentCount > 0 && (
                          <Chip
                            label={appointmentCount}
                            size="small"
                            sx={{
                              height: "18px",
                              fontSize: "0.65rem",
                              bgcolor: "#f0f7ff",
                              color: "#1976d2"
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // Render daily view
  const renderDailyView = () => (
    <Box sx={{ mb: 8 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 1 }}>
          Appointments
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {displayAppointments.length > 0 
            ? `You have ${displayAppointments.length} appointment${displayAppointments.length > 1 ? 's' : ''} scheduled for today.`
            : "No appointments scheduled for today."
          }
        </Typography>
      </Box>
      
      {displayAppointments.length > 0 ? (
        displayAppointments.map(item => renderAppointmentCard(item))
      ) : (
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center",
            py: 8
          }}
        >
          <EventAvailable sx={{ fontSize: "4rem", color: "#e0e0e0", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "#666", fontWeight: 500, mb: 1 }}>
            No Appointments
          </Typography>
          <Typography variant="body2" sx={{ color: "#999", textAlign: "center", maxWidth: "300px", mb: 3 }}>
            There are no appointments scheduled for this date. Click below to add a new appointment.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/appointment")}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              bgcolor: "#0a0d25",
              "&:hover": {
                bgcolor: "#161d4d"
              }
            }}
          >
            Add Appointment
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ 
      backgroundColor: "#f5f8fa", 
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column"
    }}>
      {/* Alert notifications */}
      <Snackbar 
        open={showAlert} 
        autoHideDuration={3000} 
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={alertType} variant="filled" sx={{ width: '100%' }}>
          <AlertTitle>{alertTitle}</AlertTitle>
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Delete confirmation dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "8px"
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {appointmentToDelete ? (
              <>
                Are you sure you want to delete the appointment for <strong>{appointmentToDelete.ptName}</strong> on {formatDateForDisplay(appointmentToDelete.appointmentDate)} at {appointmentToDelete.timeString}?
                <br /><br />
                This action cannot be undone.
              </>
            ) : (
              "Are you sure you want to delete this appointment? This action cannot be undone."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <Button 
            onClick={handleClose} 
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCloseYES} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: "8px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reminder confirmation dialog */}
      <Dialog
        open={reminderOpen}
        onClose={closeReminderDialog}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "8px"
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Send Appointment Reminder
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedPatient && (
              <>
                Are you sure you want to send a WhatsApp reminder to <strong>{selectedPatient.ptName}</strong> for their appointment on {formatDateForDisplay(selectedPatient.appointmentDate)} at {selectedPatient.timeString}?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <Button 
            onClick={closeReminderDialog} 
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmAndSendMessage} 
            variant="contained" 
            color="primary"
            startIcon={<WhatsApp />}
            sx={{ 
              borderRadius: "8px",
              bgcolor: "#25D366",
              "&:hover": {
                bgcolor: "#128C7E"
              }
            }}
          >
            Send Reminder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header */}
      <Box
        sx={{
          bgcolor: "#0a0d25",
          color: "white",
          px: 2,
          py: 2,
          borderBottomRightRadius: "20px",
          borderBottomLeftRadius: "20px",
          position: "sticky",
          top: 0,
          zIndex: 10,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
        }}
      >
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          width: "100%",
          mx: "auto",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 0 }
        }}>
          {/* Navigation */}
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            width: { xs: "100%", sm: "auto" }
          }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton 
                size="small" 
                onClick={goToPrevious}
                sx={{ color: "white", mr: 1 }}
              >
                <ArrowBackIos fontSize="small" />
              </IconButton>
              
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CalendarMonth sx={{ mr: 1, fontSize: "1.2rem" }} />
                {getHeaderTitle()}
              </Box>
              
              <IconButton 
                size="small" 
                onClick={goToNext}
                sx={{ color: "white", ml: 1 }}
              >
                <ArrowForwardIos fontSize="small" />
              </IconButton>
            </Box>
            
            <Button
              size="small"
              startIcon={<CalendarMonth />}
              onClick={handleDatePickerOpen}
              sx={{ 
                color: "white", 
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.2)"
                },
                ml: { xs: 0, sm: 2 },
                textTransform: "none",
                borderRadius: "8px",
                px: 2
              }}
            >
              Pick Date
            </Button>
          </Box>
          
          {/* View Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            aria-label="calendar view"
            size="small"
            sx={{ 
              bgcolor: "rgba(255,255,255,0.1)",
              borderRadius: "8px",
              "& .MuiToggleButtonGroup-grouped": {
                border: 0,
                color: "white",
                "&.Mui-selected": {
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white"
                },
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.15)"
                }
              }
            }}
          >
            <ToggleButton value="day" aria-label="day view">
              <ViewDay fontSize="small" sx={{ mr: 0.5 }} />
              {!isMobile && "Day"}
            </ToggleButton>
            <ToggleButton value="week" aria-label="week view">
              <ViewWeek fontSize="small" sx={{ mr: 0.5 }} />
              {!isMobile && "Week"}
            </ToggleButton>
            <ToggleButton value="month" aria-label="month view">
              <ViewModule fontSize="small" sx={{ mr: 0.5 }} />
              {!isMobile && "Month"}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 2, flex: 1 }}>
        {loading ? (
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            height: "200px" 
          }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {viewMode === "day" && renderDailyView()}
            {viewMode === "week" && renderWeeklyView()}
            {viewMode === "month" && renderMonthlyView()}
          </>
        )}
      </Container>

      {/* Date Picker Popover */}
      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={handleDatePickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
          }
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: "250px" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333" }}>
            Select Date
          </Typography>
          
          <TextField
            type="date"
            value={selectedDateForPicker.toISOString().split('T')[0]}
            onChange={handleDateChange}
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px"
              }
            }}
          />
          
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={goToToday}
              sx={{
                textTransform: "none",
                color: "#666"
              }}
            >
              Today
            </Button>
            <Button
              size="small"
              onClick={handleDatePickerClose}
              sx={{
                textTransform: "none",
                color: "#666"
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleDateConfirm}
              sx={{
                textTransform: "none",
                bgcolor: "#0a0d25",
                "&:hover": {
                  bgcolor: "#161d4d"
                }
              }}
            >
              Go to Date
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Floating Action Button */}
      <Box sx={{ position: "fixed", bottom: 88, right: 0, width: "100%", display: "flex", justifyContent: "center", zIndex: 999 }}>
        <Fab
          color="primary"
          variant="extended"
          onClick={() => navigate("/appointment")}
          sx={{
            bgcolor: "#0a0d25",
            color: "white",
            textTransform: "none",
            borderRadius: "24px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            px: 3,
            "&:hover": {
              bgcolor: "#161d4d"
            }
          }}
        >
          <Add sx={{ mr: 1 }} />
          New Appointment
        </Fab>
      </Box>
    </Box>
  );
}

export default Calendar;